import React, { forwardRef } from 'react'
import PropTypes from 'prop-types'
import { useSnackbar } from 'notistack'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Tooltip } from '@mui/material'
import { GridActionsCellItem } from '@mui/x-data-grid'
import StarIcon from '@mui/icons-material/Star'

import { Logger, LoggerTypes } from '../../../utils'
import { updateProject } from '../../../actions'
import { userRightTypes } from '../../../constants'
import { checkPermissions } from '../../../components/AccessControl'

/**
 * Round button to change the favorite status of a project.
 * Ex:
 *  - project = {name: '', id: 123, ...}
 *  - <ProjectButtonFavorite ref={ref} project={project} className'my-css-class' />
 *
 * @param {object} project Project object.
 * @param {string} className CSS class name to apply.
 * @returns {Tooltip}
 */
export const ProjectButtonFavorite = forwardRef(({ project, className }, ref) => {
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const isFavorite = project.favorite

  const onClick = (event) => {
    event.ignore = true

    let newProject = Object.assign({}, project)
    newProject.favorite = !project.favorite

    Logger.trace(LoggerTypes.CONTROLLER, 'ProjectButtonFavorite.onClick()', { project, newProject, event })

    dispatch(updateProject(newProject))
      .then((res) => {
        Logger.trace(LoggerTypes.CONTROLLER, 'ProjectButtonFavorite.onClick().dispatch().then()', { res })
      })
      .catch((res) => {
        Logger.trace(LoggerTypes.WARN, 'ProjectButtonFavorite.onClick().dispatch().catch()', { res })

        const error = (res?.response?.data && JSON.stringify(res.response.data)) || null
        enqueueSnackbar(`${t('common.api_failure')} ${error ? error : ''}`, { variant: 'error' })
      })
  }

  // Logger.trace(LoggerTypes.CONTAINER, 'ProjectButtonFavorite.render()', { project, isFavorite })

  return (
    project && (
      <Tooltip className={className} ref={ref} key={'edit'} title={t('projects.favorite.tooltip')}>
        <div>
          <GridActionsCellItem
            icon={isFavorite ? <StarIcon color="primary" /> : <StarIcon />}
            onClick={checkPermissions([userRightTypes.PROJECT_ATTRIBUTES_EDIT]) ? onClick : null}
            label={t('projects.favorite.tooltip')}
          />
        </div>
      </Tooltip>
    )
  )
})

ProjectButtonFavorite.displayName = 'ProjectButtonFavorite'

ProjectButtonFavorite.propTypes = {
  project: PropTypes.any.isRequired,
  className: PropTypes.string
}

ProjectButtonFavorite.defaultProps = {
  project: null,
  className: ''
}
